import React, { useState } from 'react';
import { Upload, Type, Send, X, ArrowLeft, Sparkles, Loader2, Briefcase } from 'lucide-react';
import api from '../api/axios';
import Swal from 'sweetalert2';

const JDModal = ({ onClose, onSuccess, initialData = null }) => {
  const [modalView, setModalView] = useState(initialData ? 'manual' : 'select');
  const [isLoading, setIsLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  
  const [formData, setFormData] = useState(initialData || { 
    title: '', role_type: 'Engineering', skills: '', full_text: '' 
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let result;
      if (initialData && initialData.id) {
        result = await api.put(`/jds/${initialData.id}`, formData);
      } else {
        result = await api.post('/jds/', formData);
      }
      onSuccess(result.data);
      onClose();
    } catch (error) { 
      Swal.fire({
        icon: 'error',
        title: 'Failed to save JD',
      });
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsLoading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      const res = await api.post('/jds/parse-pdf', uploadData);
      setFormData({ ...formData, ...res.data });
      setModalView('manual');
    } catch (error) { 
      // alert("Error parsing PDF");
      Swal.fire({
        icon: 'error',
        title: 'Failed to parse PDF',
      }); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleAIChat = async () => {
    if (!chatInput.trim()) return;
    setIsLoading(true);
    try {
      const res = await api.post('/jds/generate-ai', { message: chatInput });
      setFormData({ ...formData, ...res.data });
      setModalView('manual');
    } catch (error) { 
      // alert("AI Generation failed");
      Swal.fire({
        icon: 'error',
        title: 'AI Generation failed',
      });
    } finally { 
      setIsLoading(false); 
    }
  };

  return (
    // Dark Overlay with strong blur
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 transition-opacity">
      
      {/* Dark Glass Modal Container */}
      <div className="bg-[#0B0F19] rounded-[2rem] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col relative border border-white/10 transform scale-100 transition-transform duration-300 min-h-[500px] max-h-[90vh]">
        
        {/* --- HEADER --- */}
        <div className="px-10 pt-10 pb-6 flex justify-between items-start shrink-0 border-b border-white/5">
          <div className="flex flex-col">
            <div className="flex items-center gap-4 mb-2">
              {modalView !== 'select' && !initialData && (
                <button 
                  onClick={() => setModalView('select')} 
                  className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10"
                >
                  <ArrowLeft size={18} strokeWidth={1.5} />
                </button>
              )}
              <h2 className="text-3xl font-semibold text-white tracking-wide">
                {initialData ? "Edit Job Description" : "Create Description"}
              </h2>
            </div>
            {/* Indent only when the back button is present, so the subtitle
                stays flush with the title on the select view. */}
            <p className={`text-slate-500 text-[12px] tracking-wide font-semibold ${modalView !== 'select' && !initialData ? 'ml-14' : ''}`}>
              {modalView === 'select' && "Choose Generation Method"}
              {modalView === 'ai' && "AI Generation Engine"}
              {modalView === 'upload' && "Document Extraction"}
              {modalView === 'manual' && "Review & Finalize"}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* --- VIEW 1: SELECTION --- */}
        {modalView === 'select' && (
          <div className="px-10 pb-12 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6 flex-1">
            <button onClick={() => setModalView('ai')} className="flex flex-col text-left p-8 bg-white/5 rounded-[2rem] border border-white/5 hover:border-indigo-500/50 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 group shadow-[0_0_0_rgba(99,102,241,0)] hover:shadow-[0_0_25px_rgba(99,102,241,0.15)]">
               <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Sparkles size={24} strokeWidth={1.5} />
               </div>
               <h3 className="font-medium text-white text-xl mb-3 tracking-wide">AI Generator</h3>
               <p className="text-xs text-slate-400 leading-relaxed font-light">Describe the role briefly and let our AI draft a highly professional JD instantly.</p>
            </button>
            
            <button onClick={() => setModalView('upload')} className="flex flex-col text-left p-8 bg-white/5 rounded-[2rem] border border-white/5 hover:border-violet-500/50 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 group shadow-[0_0_0_rgba(139,92,246,0)] hover:shadow-[0_0_25px_rgba(139,92,246,0.15)]">
               <div className="w-12 h-12 bg-violet-500/10 text-violet-400 border border-violet-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Upload size={24} strokeWidth={1.5} />
               </div>
               <h3 className="font-medium text-white text-xl mb-3 tracking-wide">Upload PDF</h3>
               <p className="text-xs text-slate-400 leading-relaxed font-light">Have a document already? Upload it and we will extract the requirements for you.</p>
            </button>
            
            <button onClick={() => setModalView('manual')} className="flex flex-col text-left p-8 bg-white/5 rounded-[2rem] border border-white/5 hover:border-emerald-500/50 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 group shadow-[0_0_0_rgba(16,185,129,0)] hover:shadow-[0_0_25px_rgba(16,185,129,0.15)]">
               <div className="w-12 h-12 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Type size={24} strokeWidth={1.5} />
               </div>
               <h3 className="font-medium text-white text-xl mb-3 tracking-wide">Manual Entry</h3>
               <p className="text-xs text-slate-400 leading-relaxed font-light">Start from scratch and paste or write your job description manually.</p>
            </button>
          </div>
        )}

        {/* --- VIEW 2: AI CHAT --- */}
        {modalView === 'ai' && (
           <div className="flex flex-col flex-1 px-10 pt-6 pb-10 min-h-[400px]">
              <div className="flex-1 bg-black/30 rounded-[2rem] border border-white/5 p-8 mb-6 flex flex-col relative overflow-hidden">
                  <div className="flex-1 overflow-y-auto text-white text-xl font-light leading-relaxed">
                      {chatInput || <span className="text-slate-600">Tell me about the role you're hiring for...</span>}
                  </div>
                  {isLoading && (
                     <div className="absolute inset-0 bg-[#0B0F19]/80 backdrop-blur-sm flex flex-col items-center justify-center text-indigo-400 z-10">
                       <Loader2 className="animate-spin mb-4" size={40} strokeWidth={1.5} />
                       <span className="font-light tracking-wide text-xs">Crafting Description...</span>
                     </div>
                  )}
              </div>
              <div className="relative shrink-0">
                  <input 
                      value={chatInput} 
                      onChange={e => setChatInput(e.target.value)} 
                      onKeyDown={e => e.key === 'Enter' && handleAIChat()}
                      disabled={isLoading}
                      placeholder="e.g. Senior Python Developer with LangChain experience..."
                      className="w-full bg-black/50 border border-white/10 rounded-[2rem] pl-8 pr-16 py-5 text-sm placeholder:text-[12px] font-medium text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all disabled:opacity-60 placeholder-slate-600"
                  />
                  <button 
                      onClick={handleAIChat} 
                      disabled={isLoading || !chatInput.trim()}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 disabled:bg-white/5 disabled:text-slate-600 text-white rounded-full transition-colors shadow-[0_0_15px_rgba(99,102,241,0.3)]"
                  >
                      <Send size={18} strokeWidth={1.5} />
                  </button>
              </div>
           </div>
        )}

        {/* --- VIEW 3: UPLOAD PDF --- */}
        {modalView === 'upload' && (
          <div className="flex flex-col flex-1 px-10 pt-6 pb-10 min-h-[400px]">
            <label className="flex-1 border border-dashed border-white/20 rounded-[2.5rem] bg-black/20 hover:bg-indigo-500/5 hover:border-indigo-500/50 transition-all flex flex-col items-center justify-center cursor-pointer group relative overflow-hidden">
              {isLoading ? (
                  <div className="flex flex-col items-center text-indigo-400">
                     <Loader2 className="animate-spin mb-6" size={48} strokeWidth={1.5} />
                     <span className="font-light tracking-wide text-xs">Extracting Requirements...</span>
                  </div>
              ) : (
                  <>
                     <div className="w-20 h-20 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                       <Upload size={32} strokeWidth={1.5} />
                     </div>
                     <span className="font-light text-white text-2xl mb-2 tracking-wide">Click to Upload PDF</span>
                     <span className="text-slate-500 text-sm font-light">Drag and drop your file here, or browse</span>
                     <input type="file" accept=".pdf" className="hidden" onChange={handleFileUpload} />
                  </>
              )}
            </label>
          </div>
        )}

        {/* --- VIEW 4: MANUAL FORM --- */}
        {modalView === 'manual' && (
          <form onSubmit={handleSave} className="flex flex-col flex-1 px-10 pt-6 pb-10 overflow-y-auto min-h-[400px] custom-scrollbar">
            <div className="space-y-6 flex-1 flex flex-col">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Briefcase size={14} className="text-indigo-400"/> Role Title</label>
                  <input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="e.g. Senior Frontend Engineer" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Sparkles size={14} className="text-indigo-400"/> Top Skills</label>
                  <input required value={formData.skills} onChange={e => setFormData({...formData, skills: e.target.value})} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="e.g. React, Node.js, TypeScript" />
                </div>
              </div>
              
              <div className="h-full flex flex-col flex-1">
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Type size={14} className="text-indigo-400"/> Full Description</label>
                <textarea required value={formData.full_text} onChange={e => setFormData({...formData, full_text: e.target.value})} className="w-full flex-1 bg-black/50 border border-white/10 rounded-[2rem] px-6 py-6 text-slate-300 font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all min-h-[250px] resize-none leading-relaxed custom-scrollbar placeholder-slate-600" placeholder="Detailed responsibilities, requirements, and benefits..." />
              </div>
            </div>

            <div className="flex justify-end pt-8 shrink-0 border-t border-white/5 mt-8">
              <button type="submit" disabled={isLoading} className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-3.5 rounded-2xl text-xs font-bold tracking-wide transition-all shadow-[0_0_15px_rgba(99,102,241,0.4)] active:scale-95 flex items-center gap-3 disabled:opacity-50">
                {isLoading && <Loader2 className="animate-spin" size={16}/>}
                {initialData ? "Save Changes" : "Publish Record"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default JDModal;